import * as z from 'zod';
import { isOrderWindowWithinHours } from '@noor/eats-api';
import { MENU_SOURCES } from '@/entities/branch';

/** Translator shape (i18next's `t`), passed in so validation messages localize. */
type Translate = (key: string) => string;

/**
 * One of the branch's two photos while it is being edited. `file` is present
 * only for a freshly-picked upload; `url` is a remote URL (already saved) or a
 * `blob:` preview (new).
 *
 * `saved` marks a photo already stored on the branch. Its `url` is the
 * backend's one-way expansion of a storage key, so it can be shown and removed
 * but never re-sent — a submit that doesn't re-pick simply leaves the slot
 * alone.
 */
export const branchPhotoSchema = z.object({
  url: z.string(),
  file: z.instanceof(File).optional(),
  saved: z.boolean().optional(),
});
export type BranchFormPhoto = z.infer<typeof branchPhotoSchema>;

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Build the branch form schema with localized messages.
 *
 * `name` is captured only in Russian: the design's «Наименование Ресторана» is
 * a single input, and the API's `LocalizedText` accepts a partial set (an empty
 * member reads as "not provided"). Descriptions ARE captured per language,
 * matching the three «Описание» inputs in the design.
 *
 * Coordinates come from the map picker, not from typing, so they are validated
 * as a PAIR (see the `superRefine` below) rather than surfaced as their own
 * fields — there is no input to attach a per-field message to.
 */
export function buildBranchFormSchema(t: Translate, requirePhotos = true) {
  const time = z.string().regex(HHMM, t('branches.form.timeInvalid'));

  return (
    z
      .object({
        name: z.string().trim().min(1, t('branches.form.nameRequired')),
        phone: z.string().trim().min(1, t('branches.form.phoneRequired')),
        /** Street address as typed; the map picker fills it in and can be edited. */
        address: z.string().trim().min(1, t('branches.form.addressRequired')),
        // Bare numbers here: "unplaced" is `0, 0`, which is a perfectly valid
        // number, so the requirement cannot be expressed per-field. The pair is
        // checked in the `superRefine` below.
        latitude: z.number(),
        longitude: z.number(),
        icon: branchPhotoSchema.optional(),
        backgroundImage: branchPhotoSchema.optional(),
        minPrepTime: z.preprocess(
          (value) =>
            value === '' || value === null || value === undefined
              ? undefined
              : Number(value),
          z
            .number({ error: t('branches.form.prepTimeRequired') })
            .int()
            .nonnegative(t('branches.form.prepTimeRequired')),
        ),
        maxPrepTime: z.preprocess(
          (value) =>
            value === '' || value === null || value === undefined
              ? undefined
              : Number(value),
          z
            .number({ error: t('branches.form.prepTimeRequired') })
            .int()
            .nonnegative(t('branches.form.prepTimeRequired')),
        ),
        /** Restaurant discovery rubrics selected from Restaurant Category Service. */
        restaurantCategoryIds: z.array(z.string()),
        cuisineIds: z.array(z.string()),
        pickupAvailable: z.boolean(),
        /**
         * Free-form on the wire, but the backend accepts a fixed set — see
         * `entities/branch/config/menu-sources.ts`. `''` is allowed and means
         * "not chosen": the mapper omits an empty value, so an untouched field
         * leaves whatever the branch already stores alone.
         */
        menuSource: z.union([z.enum(MENU_SOURCES), z.literal('')]).optional(),
        /**
         * Minimum basket total for free delivery, in so'm. Cleared arrives as `''`
         * and normalizes to undefined so "untouched" stays distinguishable from a
         * real 0 (which the API reads as "no free-delivery offer").
         */
        freeDeliveryThreshold: z.preprocess(
          (value) =>
            value === '' || value === null || value === undefined
              ? undefined
              : Number(value),
          z.number().int().nonnegative(t('branches.form.thresholdInvalid')).optional(),
        ),
        /**
         * Minimum order total in so'm. Empty (or 0) means "no minimum" and
         * normalizes to undefined; on update that is sent as an explicit `null`
         * so a merchant can clear a minimum they set earlier.
         */
        minOrderAmount: z.preprocess(
          (value) =>
            value === '' || value === null || value === undefined || Number(value) === 0
              ? undefined
              : Number(value),
          z.number().int().positive(t('branches.form.thresholdInvalid')).optional(),
        ),
        descriptionRu: z.string().trim().min(1, t('branches.form.descriptionRequired')),
        descriptionUz: z.string().trim().min(1, t('branches.form.descriptionRequired')),
        descriptionEn: z.string().trim().min(1, t('branches.form.descriptionRequired')),
        /**
         * Seven rows, Monday-first. A row's `isOpen` mirrors the design's checkbox;
         * times must be valid HH:MM values, but the UI deliberately allows any
         * open/close combination so merchants can model overnight windows.
         */
        days: z
          .array(
            z
              .object({
                label: z.string(),
                isOpen: z.boolean(),
                open: time,
                close: z.union([time, z.literal('24:00')]),
                orderFrom: z.string().optional(),
                orderTo: z.string().optional(),
              })
              .superRefine((day, ctx) => {
                if (
                  day.isOpen &&
                  !isOrderWindowWithinHours(
                    day.open,
                    day.close,
                    day.orderFrom,
                    day.orderTo,
                  )
                ) {
                  ctx.addIssue({
                    code: 'custom',
                    path: [day.orderFrom ? 'orderTo' : 'orderFrom'],
                    message: t('branches.form.orderWindowInvalid'),
                  });
                }
              }),
          )
          .length(7),
      })
      /**
       * The branch must actually be placed on the map.
       *
       * `emptyDefaults()` seeds `0, 0` for an unpicked location, and `z.number()`
       * accepts that happily — so without this a branch could be created with no
       * real coordinates at all, landing at Null Island on every map that plots
       * it. Null Island is also a legitimate point on paper, which is why this
       * tests the PAIR rather than each field: `0, 0` is the sentinel the form
       * uses for "never picked", and no branch in Uzbekistan is at either zero.
       *
       * The message is attached to `address`, not to a coordinate field: the
       * coordinates have no input of their own, and the «Указать на карте»
       * picker that sets them lives inside the address row — so this is where
       * the merchant is already looking for the control that fixes it.
       */
      .superRefine((values, ctx) => {
        for (const [field, photo] of requirePhotos
          ? ([
              ['icon', values.icon],
              ['backgroundImage', values.backgroundImage],
            ] as const)
          : []) {
          if (!photo?.url) {
            ctx.addIssue({
              code: 'custom',
              path: [field],
              message: t('branches.form.photoRequired'),
            });
          }
        }
        if (values.minPrepTime > values.maxPrepTime) {
          ctx.addIssue({
            code: 'custom',
            path: ['maxPrepTime'],
            message: t('branches.form.prepTimeRangeInvalid'),
          });
        }
        if (values.latitude === 0 && values.longitude === 0) {
          ctx.addIssue({
            code: 'custom',
            path: ['address'],
            message: t('branches.form.locationRequired'),
          });
        }
      })
  );
}

export type BranchFormValues = z.infer<ReturnType<typeof buildBranchFormSchema>>;

/**
 * Payloads for the add/edit-branch modals, validated at the modal registry
 * boundary. The `mode` discriminant is what makes `branchId` required exactly
 * when editing — a create carries none, and an edit cannot be opened without
 * one.
 */
export const addBranchPayloadSchema = z.object({
  mode: z.literal('add'),
});
export type AddBranchPayload = z.infer<typeof addBranchPayloadSchema>;

export const editBranchPayloadSchema = z.object({
  mode: z.literal('edit'),
  /** The branch being edited; the modal re-reads it by id for translations. */
  branchId: z.string(),
});
export type EditBranchPayload = z.infer<typeof editBranchPayloadSchema>;

/**
 * One product attached to the branch in step 2 of the wizard (Figma
 * 1067:12534): the product's identity plus the price THIS branch charges for
 * it.
 *
 * `price` is the branch-local overlay (`v1RestaurantProductInput.price`), not
 * the product's shared base price, and `stockQuantity` is the optional
 * branch-local count (`stock_quantity`) for restaurants that track remaining
 * items. The same product can cost and stock differently in two branches,
 * which is the whole reason the overlay exists.
 *
 * `attached` marks a row the branch ALREADY stocks. It is what makes the
 * submit a diff rather than a blind re-attach: an untouched attached row needs
 * no write, a new row is a POST, and a removed one a DELETE. `null` price is
 * "not entered", kept distinct from a real `0` (a giveaway item).
 */
export const branchProductSchema = z.object({
  productId: z.string(),
  name: z.string(),
  /** Cover photo as an absolute URL, for the picker row's 64px thumbnail. */
  imageUrl: z.string().optional(),
  price: z.number().int().nonnegative().nullable(),
  stockQuantity: z.number().int().nonnegative().nullable(),
  /** Whether the branch already stocks this product (an edit's seeded rows). */
  attached: z.boolean().optional(),
  /**
   * The BRANCH's own selling hours ("HH:MM"). Absent/empty = the branch sets
   * none and inherits the dish's, so an inherited value is never written back
   * as if the branch had chosen it.
   */
  availableFrom: z.string().optional(),
  availableTo: z.string().optional(),
});
export type BranchProduct = z.infer<typeof branchProductSchema>;
